/**
 * UnlinkedTripsBanner
 * Shows a collapsible warning when contract-linkable trips don't have a contract assigned.
 * Each unlinked trip row has a "Link Contract" CTA that opens the trip for editing.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { AlertTriangle, ChevronDown, ChevronUp, Link2 } from 'lucide-react';

// Trip types that should be linked to a contract
const CONTRACT_LINKABLE_TYPES = new Set([
  'work_trip',
  'base_work_uk',
  'base_work_foreign',
  'hotel_stay',
  'uk_hotel_stay',
  'course_uk',
  'course_foreign',
  'visa_run',
  'paid_leave',
]);

const TRIP_TYPE_LABELS = {
  work_trip: 'At Sea / On Vessel',
  base_work_uk: 'Base Work (UK)',
  base_work_foreign: 'Base Work (Abroad)',
  hotel_stay: 'Hotel Stay (Abroad)',
  uk_hotel_stay: 'Hotel Stay (UK)',
  course_uk: 'Course (UK)',
  course_foreign: 'Course (Abroad)',
  visa_run: 'Visa Run',
  paid_leave: 'Paid Annual Leave',
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch {
    return d;
  }
};

const getTripLabel = (trip) => {
  return (
    trip.vessel_name
    || trip.hotel_name
    || trip.course_name
    || trip.base_name
    || TRIP_TYPE_LABELS[trip.trip_type]
    || 'Untitled trip'
  );
};

const getTripDateRange = (trip) => {
  const start = trip.date_left_uk || trip.date_joined_vessel || trip.start_date;
  const end = trip.date_returned_uk || trip.date_left_vessel || trip.end_date;
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
  if (start) return formatDate(start);
  return '';
};

const UnlinkedTripsBanner = ({ trips, onEdit }) => {
  const [expanded, setExpanded] = useState(false);

  const unlinkedTrips = useMemo(() => {
    return (trips || []).filter(
      (t) => CONTRACT_LINKABLE_TYPES.has(t.trip_type) && !t.contract_id
    );
  }, [trips]);

  if (unlinkedTrips.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-amber-500/30 bg-amber-500/10 overflow-hidden"
      data-testid="unlinked-trips-banner"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-amber-500/5 transition-colors"
        data-testid="unlinked-trips-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-300">
              {unlinkedTrips.length} {unlinkedTrips.length === 1 ? 'trip' : 'trips'} with no contract selected
            </p>
            <p className="text-xs text-amber-200/70 mt-0.5">
              Link each trip to a contract so earnings are correctly attributed in reports.
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-amber-400 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-amber-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-amber-500/20 divide-y divide-amber-500/10">
          {unlinkedTrips.map((trip) => (
            <div
              key={trip.id}
              className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-amber-500/5"
              data-testid={`unlinked-trip-${trip.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{getTripLabel(trip)}</p>
                <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                  <span className="text-amber-400/90">
                    {TRIP_TYPE_LABELS[trip.trip_type] || trip.trip_type}
                  </span>
                  {getTripDateRange(trip) && (
                    <>
                      <span className="text-slate-600">•</span>
                      <span>{getTripDateRange(trip)}</span>
                    </>
                  )}
                  <span className="text-slate-600">•</span>
                  <span className="italic text-slate-500">no contract selected</span>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(trip)}
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/15 hover:text-amber-200 h-8 text-xs shrink-0"
                data-testid={`link-contract-btn-${trip.id}`}
              >
                <Link2 className="h-3.5 w-3.5 mr-1.5" />
                Link Contract
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnlinkedTripsBanner;
